import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api/client';
import { Section, SectionEyebrow, SectionTitle } from '../components/ui/Section';
import { PillButton } from '../components/ui/PillButton';
import { TextInput, TextArea } from '../components/ui/TextInput';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Chip } from '../components/ui/Chip';
import { useAuth } from '../lib/auth/store';
import { useFocusTimer } from '../lib/focus/store';
import { formatDate, formatTime } from '../lib/format';
import { useDialog } from '../lib/dialog/hooks';
import { SimpleBarChart } from '../components/charts/SimpleBarChart';
import type { Task, TaskStatus } from '@mindrift/shared';

export const Route = createFileRoute('/_app/todos')({
  component: TodosPage,
});

const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];

function TodosPage() {
  const settings = useAuth((s) => s.settings);
  const qc = useQueryClient();
  const router = useRouter();
  const dialog = useDialog();
  const setPendingTask = useFocusTimer((s) => s.setPendingTask);
  const focusStatus = useFocusTimer((s) => s.status);
  const [filter, setFilter] = useState<'all' | TaskStatus>('all');
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  const [range, setRange] = useState<Range>(30);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', filter, areaFilter],
    queryFn: () => api.tasks.list({
      status: filter === 'all' ? undefined : filter,
      area: areaFilter ?? undefined,
    }),
  });

  const { data: trend } = useQuery({
    queryKey: ['tasks', 'trend', range],
    queryFn: () => api.tasks.trend(range),
  });

  const toggle = useMutation({
    mutationFn: (task: Task) => api.tasks.update(task.id, { status: task.status === 'completed' ? 'in_progress' : 'completed' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.tasks.remove(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      if (expandedId === id) setExpandedId(null);
    },
  });

  const update = useMutation({
    mutationFn: (input: { id: string; title: string; description: string | null; area: string; dueDate: string | null }) =>
      api.tasks.update(input.id, {
        title: input.title,
        description: input.description,
        area: input.area,
        dueDate: input.dueDate,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      setEditingId(null);
    },
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', area: 'Personal', dueDate: '' });
  const areas = ['Work', 'Personal', 'Health', 'Social', ...(settings?.customAreas ?? [])];

  const create = useMutation({
    mutationFn: () => api.tasks.create({
      title: form.title.trim(),
      description: form.description.trim() || null,
      area: form.area,
      dueDate: form.dueDate || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      setForm({ title: '', description: '', area: 'Personal', dueDate: '' });
      setShowForm(false);
    },
  });

  const tasks = data?.tasks ?? [];
  const counts = {
    open: tasks.filter((t) => t.status === 'in_progress').length,
    done: tasks.filter((t) => t.status === 'completed').length,
    overdue: tasks.filter((t) => t.status === 'overdue').length,
  };

  return (
    <>
      <Section surface="canvas">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <SectionEyebrow>TODOS</SectionEyebrow>
            <SectionTitle size="section-heading" className="mt-3">A list of what to do next.</SectionTitle>
          </div>
          <PillButton onClick={() => setShowForm((v) => !v)}>{showForm ? 'Close' : 'New task'}</PillButton>
        </div>

        {showForm && (
          <Card className="mt-8">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (form.title.trim()) create.mutate();
              }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <TextInput
                label="Title"
                autoFocus
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="md:col-span-2"
              />
              <TextArea
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="md:col-span-2"
                rows={3}
              />
              <div className="flex flex-col gap-1.5">
                <label className="field-label">Area</label>
                <select
                  className="rounded-md border border-hairline px-3 py-3 text-body bg-canvas-white"
                  value={form.area}
                  onChange={(e) => setForm({ ...form, area: e.target.value })}
                >
                  {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <TextInput
                label="Due date"
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              />
              <div className="md:col-span-2 flex gap-2 justify-end">
                <PillButton variant="ghost" onClick={() => setShowForm(false)} type="button">Cancel</PillButton>
                <PillButton type="submit" loading={create.isPending} disabled={!form.title.trim()}>Save task</PillButton>
              </div>
            </form>
          </Card>
        )}

        <div className="mt-8 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {(['all', 'in_progress', 'completed', 'overdue'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`pill-outline text-caption ${filter === f ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
                type="button"
              >
                {f === 'all' ? 'All' : f === 'in_progress' ? 'In progress' : f === 'completed' ? 'Completed' : 'Overdue'}
              </button>
            ))}
            <div className="flex-1" />
            <div className="flex gap-3 text-caption text-muted-slate">
              <span>{counts.open} open</span>
              <span>·</span>
              <span>{counts.done} done</span>
              {counts.overdue > 0 && (
                <>
                  <span>·</span>
                  <span className="text-error-red">{counts.overdue} overdue</span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {areas.map((a) => (
              <button
                key={a}
                onClick={() => setAreaFilter(areaFilter === a ? null : a)}
                className={`pill-outline text-caption ${areaFilter === a ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
                type="button"
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <p className="text-muted-slate">Loading…</p>
          ) : tasks.length === 0 ? (
            <EmptyState
              title="No tasks here."
              description="Add one to start. Or move on — sometimes the empty list is the point."
            />
          ) : (
            <ul className="border-t border-hairline">
              {tasks.map((t) =>
                editingId === t.id ? (
                  <TaskEditRow
                    key={t.id}
                    task={t}
                    areas={areas}
                    saving={update.isPending}
                    onSave={(payload) => update.mutate({ id: t.id, ...payload })}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <li key={t.id} className="border-b border-hairline">
                    <button
                      type="button"
                      onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                      className="w-full text-left py-4 flex items-start gap-4 hover:bg-pale-blue-wash/30 transition-colors"
                      aria-expanded={expandedId === t.id}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-body ${t.status === 'completed' ? 'line-through text-muted-slate' : 'text-ink'}`}>{t.title}</p>
                        {t.description && <p className="text-caption text-muted-slate mt-1 line-clamp-2">{t.description}</p>}
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-caption text-muted-slate">
                          <Chip>{t.area}</Chip>
                          {t.dueDate && <span>due {formatDate(t.dueDate)}</span>}
                          {t.completedAt && <span>· done {formatDate(t.completedAt)}</span>}
                        </div>
                      </div>
                      <span
                        className="text-caption text-muted-slate shrink-0 self-center"
                        aria-hidden
                      >
                        {expandedId === t.id ? '▴' : '▾'}
                      </span>
                    </button>

                    {expandedId === t.id && (
                      <TaskDetail
                        task={t}
                        onEdit={() => setEditingId(t.id)}
                        onDelete={async () => {
                          const ok = await dialog.confirm(
                            'Delete this task?',
                            'This cannot be undone.',
                            { tone: 'danger', confirmLabel: 'Delete' },
                          );
                          if (ok) remove.mutate(t.id);
                        }}
                        onToggle={() => toggle.mutate(t)}
                        onStartFocus={() => {
                          setPendingTask(t.id);
                          router.navigate({ to: '/focus' });
                        }}
                        focusRunning={focusStatus === 'running'}
                        onClose={() => setExpandedId(null)}
                      />
                    )}
                  </li>
                )
              )}
            </ul>
          )}
        </div>
      </Section>

      <Section surface="pale-blue">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <SectionEyebrow>TREND</SectionEyebrow>
            <SectionTitle size="card-heading" className="mt-1">Tasks over time</SectionTitle>
          </div>
          <div className="flex gap-1 shrink-0">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`pill-outline text-caption ${range === r ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
                type="button"
              >
                {r}D
              </button>
            ))}
          </div>
        </div>
        {trend && trend.series.length > 0 ? (
          <SimpleBarChart
            data={trend.series}
            series={[
              { key: 'added', label: 'Added', color: '#1863dc' },
              { key: 'completed', label: 'Completed', color: '#003c33' },
              { key: 'overdue', label: 'Overdue', color: '#b30000' },
            ]}
          />
        ) : (
          <p className="text-muted-slate">No task activity in this range yet.</p>
        )}
      </Section>
    </>
  );
}

function TaskDetail({
  task,
  onEdit,
  onDelete,
  onToggle,
  onStartFocus,
  focusRunning,
  onClose,
}: {
  task: Task;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  onStartFocus: () => void;
  focusRunning: boolean;
  onClose: () => void;
}) {
  return (
    <div className="pl-9 pr-4 pb-5 pt-1 bg-pale-blue-wash/30 border-t border-hairline/60">
      <div className="flex flex-col gap-4 mt-3">
        {task.description ? (
          <div>
            <p className="field-label">DESCRIPTION</p>
            <p className="mt-1.5 text-body text-ink whitespace-pre-wrap leading-relaxed">{task.description}</p>
          </div>
        ) : (
          <p className="text-caption text-muted-slate italic">No description.</p>
        )}

        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-caption">
          <div>
            <dt className="field-label">AREA</dt>
            <dd className="mt-1"><Chip>{task.area}</Chip></dd>
          </div>
          <div>
            <dt className="field-label">DUE</dt>
            <dd className="mt-1 text-ink">{task.dueDate ? formatDate(task.dueDate) : <span className="text-muted-slate">—</span>}</dd>
          </div>
          <div>
            <dt className="field-label">CREATED</dt>
            <dd className="mt-1 text-ink">{formatDate(task.createdAt)}</dd>
          </div>
          <div>
            <dt className="field-label">STATUS</dt>
            <dd className="mt-1 text-ink">
              {task.status === 'in_progress' ? 'In progress' : task.status === 'completed' ? 'Completed' : 'Overdue'}
              {task.completedAt && <span className="text-muted-slate"> · {formatTime(task.completedAt)}</span>}
            </dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-hairline">
          {task.status !== 'completed' ? (
            <PillButton onClick={onToggle} disabled={task.status === 'overdue'}>
              Mark as completed
            </PillButton>
          ) : (
            <PillButton variant="outline" onClick={onToggle}>
              Mark in progress
            </PillButton>
          )}
          {task.status !== 'completed' && (
            <PillButton
              variant="outline"
              onClick={onStartFocus}
              disabled={focusRunning}
              title={focusRunning ? 'A focus session is already running' : 'Start a focus session on this task'}
            >
              {focusRunning ? 'Focus in progress' : 'Start focus session'}
            </PillButton>
          )}
          <PillButton variant="ghost" onClick={onEdit}>Edit</PillButton>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="text-caption text-muted-slate hover:text-ink px-3 py-2"
            aria-label="Close detail"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-caption text-muted-slate hover:text-error-red px-3 py-2"
            aria-label="Delete task"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskEditRow({
  task,
  areas,
  saving,
  onSave,
  onCancel,
}: {
  task: Task;
  areas: string[];
  saving: boolean;
  onSave: (payload: { title: string; description: string | null; area: string; dueDate: string | null }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [area, setArea] = useState(task.area);
  const [dueDate, setDueDate] = useState(task.dueDate ?? '');

  return (
    <li className="border-b border-hairline py-4 bg-pale-blue-wash/30">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) {
            onSave({
              title: title.trim(),
              description: description.trim() || null,
              area,
              dueDate: dueDate || null,
            });
          }
        }}
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
      >
        <TextInput
          label="Title"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="md:col-span-2"
        />
        <TextArea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="md:col-span-2"
          rows={2}
        />
        <div className="flex flex-col gap-1.5">
          <label className="field-label">Area</label>
          <select
            className="rounded-md border border-hairline px-3 py-3 text-body bg-canvas-white"
            value={area}
            onChange={(e) => setArea(e.target.value)}
          >
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <TextInput
          label="Due date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        <div className="md:col-span-2 flex gap-2 justify-end">
          <PillButton variant="ghost" onClick={onCancel} type="button">Cancel</PillButton>
          <PillButton type="submit" loading={saving} disabled={!title.trim()}>Save changes</PillButton>
        </div>
      </form>
    </li>
  );
}
